import { useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type Toast, type ToastVariant, useToastStore } from '@/store/toastStore';

const ICON: Record<ToastVariant, typeof Info> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const ACCENT: Record<ToastVariant, string> = {
  success: '#00C875',
  error: '#E2445C',
  info: '#0073EA',
  warning: '#F97316',
};

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((s) => s.dismiss);
  const Icon = ICON[toast.variant];

  useEffect(() => {
    const t = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(t);
  }, [toast.id, toast.duration, dismiss]);

  return (
    <div
      role="status"
      className="flex w-80 items-start gap-3 rounded-lg border border-border bg-surface-secondary px-4 py-3 shadow-lg animate-slide-up"
    >
      <Icon className="mt-0.5 size-5 shrink-0" style={{ color: ACCENT[toast.variant] }} />
      <p className="flex-1 text-sm text-content-primary">{toast.message}</p>
      <button
        onClick={() => dismiss(toast.id)}
        className="shrink-0 rounded p-0.5 text-content-muted hover:bg-surface-hover hover:text-content-primary"
        aria-label="Fermer"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

/** Conteneur global des notifications toast (monté une fois dans App). */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <div className={cn('pointer-events-none fixed bottom-5 right-5 z-[100] flex flex-col gap-2')}>
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} />
        </div>
      ))}
    </div>
  );
}
